import { useState } from "react";

import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useClinic } from "client/clinics/hooks";
import CatalogFilters from "client/home/components/CatalogFilters";
import { ProductLineListSource } from "client/analytics/types";

import BrowseSupport from "./BrowseSupport";
import { useCarouselDisplayQuery } from "../hooks";
import PreFooter from "./PreFooter";
import CategoryCardV2 from "./CategoryCardV2";
import { CategoryCarouselSection, TopSellerCarouselSection } from "./CarouselSections";
import { partitionCarousels } from "../utils";
import { BrowseAllPatientKind, IBrowseFilterSpeciesKind } from "../types";

import styles from "../styles/BrowseCatalog.module.less";

const BrowseCatalogV2 = () => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  const clinic = useClinic();
  const {
    data: display,
    isLoading,
    error,
  } = useCarouselDisplayQuery({ species }, { enabled: true });

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !display) {
    return <FetchErrorContainer error={error} entityName="categories" isPlural={true} />;
  }

  const { topSellerCarousel, categoryCarousels } = partitionCarousels(display.carousels);

  return (
    <div>
      <Body>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.titles}>
              <div className={styles.label}>Welcome to our new home delivery portal</div>
              <h1 className={styles.title}>Browse online store</h1>
            </div>
            <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
          </div>
          <div className={styles.subheading}>
            <HorizontalOverflow
              direction="right"
              classNames={{ scroller: styles.categories, next: styles.next, back: styles.back }}
            >
              {categoryCarousels.map((c, i) => (
                <CategoryCardV2.Small
                  key={c.category.id}
                  category={c.category}
                  index={i}
                  className={styles.smallCard}
                />
              ))}
            </HorizontalOverflow>
          </div>
        </div>
      </Body>
      <div className={styles.main}>
        <Container>
          <div className={styles.categories}>
            {topSellerCarousel && (
              <TopSellerCarouselSection
                productLines={topSellerCarousel.product_lines}
                eventViewName={ProductLineListSource.BrowseCatalogFeatured}
                classNames={styles}
              />
            )}
            {categoryCarousels.map((c, i) => (
              <CategoryCarouselSection
                key={c.category.id}
                category={c.category}
                index={i}
                productLines={c.product_lines}
                eventViewName={ProductLineListSource.BrowseCatalogCategory}
                classNames={styles}
              />
            ))}
          </div>
        </Container>
      </div>
      <div className={styles.support}>
        <Container>
          <BrowseSupport />
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </div>
  );
};

export default BrowseCatalogV2;
